import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useBuilding } from "../../context/BuildingContext";
import { statementsApi, unitsApi } from "../../services/api";
import Spinner from "../../components/common/Spinner";
import ErrorAlert from "../../components/common/ErrorAlert";
import PageHeader from "../../components/common/PageHeader";
import { formatCurrency } from "../../utils/formatters";
import { StatementItem } from "../../modules/statementTypes";
import { Unit } from "../../modules/unitTypes";

interface DraftItem extends StatementItem {}

const newItem = (): DraftItem => ({
  id:
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `item-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  description: "",
  category: "",
  amount: 0,
});

const today = () => new Date().toISOString().split("T")[0];
const inDays = (days: number) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
};

const StatementCreate: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentBuilding } = useBuilding();

  const [units, setUnits] = useState<Unit[]>([]);
  const [loadingUnits, setLoadingUnits] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const [unitId, setUnitId] = useState<string>(
    searchParams.get("unitId") || "",
  );
  const [period, setPeriod] = useState<string>("");
  const [issueDate, setIssueDate] = useState<string>(today());
  const [dueDate, setDueDate] = useState<string>(inDays(30));
  const [notes, setNotes] = useState<string>("");
  const [items, setItems] = useState<DraftItem[]>([newItem()]);

  // Load units for the current building so the user can pick a unit/resident
  useEffect(() => {
    const fetchUnits = async () => {
      if (!currentBuilding?.id) return;
      try {
        setLoadingUnits(true);
        const data = await unitsApi.getAll(currentBuilding.id);
        setUnits(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error fetching units:", err);
        setError("Failed to load units. Please try again later.");
      } finally {
        setLoadingUnits(false);
      }
    };
    fetchUnits();
  }, [currentBuilding?.id]);

  const selectedUnit = useMemo(
    () => units.find((u) => u.id === unitId),
    [units, unitId],
  );

  const primaryResident = useMemo(() => {
    if (!selectedUnit?.residents?.length) return undefined;
    return (
      selectedUnit.residents.find((r) => r.isPrimary) ||
      selectedUnit.residents[0]
    );
  }, [selectedUnit]);

  const totalAmount = useMemo(
    () => items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0),
    [items],
  );

  const handleItemChange = (
    id: string,
    field: keyof DraftItem,
    value: string,
  ) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              [field]: field === "amount" ? parseFloat(value) || 0 : value,
            }
          : item,
      ),
    );
  };

  const handleAddItem = () => setItems((prev) => [...prev, newItem()]);

  const handleRemoveItem = (id: string) =>
    setItems((prev) =>
      prev.length > 1 ? prev.filter((item) => item.id !== id) : prev,
    );

  const validate = (): string | null => {
    if (!currentBuilding?.id) return "Please select a building first.";
    if (!unitId) return "Please select a unit.";
    if (!period.trim()) return "Please enter a statement period.";
    if (!issueDate) return "Please choose an issue date.";
    if (!dueDate) return "Please choose a due date.";
    const validItems = items.filter((item) => item.description.trim());
    if (validItems.length === 0)
      return "Add at least one line item with a description.";
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    const validationError = validate();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    const cleanItems = items
      .filter((item) => item.description.trim())
      .map((item) => ({
        id: item.id,
        description: item.description.trim(),
        category: item.category.trim(),
        amount: Number(item.amount) || 0,
      }));

    try {
      setIsSubmitting(true);
      const payload = {
        buildingId: currentBuilding!.id,
        unitId,
        unitName: selectedUnit?.name || "",
        residentName: primaryResident?.name || "",
        residentEmail: primaryResident?.email || undefined,
        period: period.trim(),
        issueDate,
        dueDate,
        status: "draft" as const,
        items: cleanItems,
        totalAmount,
        notes: notes.trim() || undefined,
      };

      const created = await statementsApi.create(payload);
      if (created?.id) {
        navigate(`/dashboard/statements/${created.id}`);
      } else {
        navigate("/dashboard/statements");
      }
    } catch (err) {
      console.error("Error creating statement:", err);
      setFormError("Failed to create statement. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => navigate("/dashboard/statements");

  if (loadingUnits) {
    return <Spinner message="Loading..." />;
  }

  if (error) {
    return <ErrorAlert message={error} />;
  }

  return (
    <div>
      <PageHeader
        title="Create Statement"
        description={`Create a new service charge statement for ${
          currentBuilding?.name || "your building"
        }`}
      />

      {formError && (
        <div className="mt-4">
          <ErrorAlert message={formError} />
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="mt-8 bg-white shadow rounded-lg p-6 space-y-6"
      >
        {/* Statement details */}
        <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
          <div className="sm:col-span-3">
            <label
              htmlFor="unitId"
              className="block text-sm font-medium text-gray-700"
            >
              Unit
            </label>
            <select
              id="unitId"
              name="unitId"
              value={unitId}
              onChange={(e) => setUnitId(e.target.value)}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
            >
              <option value="">Select a unit...</option>
              {units.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {unit.name}
                </option>
              ))}
            </select>
            {primaryResident && (
              <p className="mt-1 text-sm text-gray-500">
                Resident: {primaryResident.name}
                {primaryResident.email ? ` (${primaryResident.email})` : ""}
              </p>
            )}
          </div>

          <div className="sm:col-span-3">
            <label
              htmlFor="period"
              className="block text-sm font-medium text-gray-700"
            >
              Period
            </label>
            <input
              type="text"
              id="period"
              name="period"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              placeholder="e.g. Q1 2026 or January 2026"
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>

          <div className="sm:col-span-3">
            <label
              htmlFor="issueDate"
              className="block text-sm font-medium text-gray-700"
            >
              Issue Date
            </label>
            <input
              type="date"
              id="issueDate"
              name="issueDate"
              value={issueDate}
              onChange={(e) => setIssueDate(e.target.value)}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>

          <div className="sm:col-span-3">
            <label
              htmlFor="dueDate"
              className="block text-sm font-medium text-gray-700"
            >
              Due Date
            </label>
            <input
              type="date"
              id="dueDate"
              name="dueDate"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>
        </div>

        {/* Line items */}
        <div>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              Line Items
            </h3>
            <button
              type="button"
              onClick={handleAddItem}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Add Item
            </button>
          </div>

          <div className="mt-4 space-y-3">
            {items.map((item) => (
              <div
                key={item.id}
                className="grid grid-cols-1 gap-3 sm:grid-cols-12 items-end"
              >
                <div className="sm:col-span-6">
                  <label className="block text-xs font-medium text-gray-500">
                    Description
                  </label>
                  <input
                    type="text"
                    value={item.description}
                    onChange={(e) =>
                      handleItemChange(item.id, "description", e.target.value)
                    }
                    placeholder="e.g. Service charge"
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>
                <div className="sm:col-span-3">
                  <label className="block text-xs font-medium text-gray-500">
                    Category
                  </label>
                  <input
                    type="text"
                    value={item.category}
                    onChange={(e) =>
                      handleItemChange(item.id, "category", e.target.value)
                    }
                    placeholder="e.g. Maintenance"
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-gray-500">
                    Amount (£)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={item.amount === 0 ? "" : item.amount}
                    onChange={(e) =>
                      handleItemChange(item.id, "amount", e.target.value)
                    }
                    placeholder="0.00"
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>
                <div className="sm:col-span-1 flex justify-end">
                  <button
                    type="button"
                    onClick={() => handleRemoveItem(item.id)}
                    disabled={items.length === 1}
                    className="text-sm text-red-600 hover:text-red-800 disabled:text-gray-300 disabled:cursor-not-allowed"
                    aria-label="Remove item"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 flex justify-end border-t border-gray-200 pt-4">
            <p className="text-sm font-medium text-gray-900">
              Total: {formatCurrency(totalAmount)}
            </p>
          </div>
        </div>

        {/* Notes */}
        <div>
          <label
            htmlFor="notes"
            className="block text-sm font-medium text-gray-700"
          >
            Notes
          </label>
          <textarea
            id="notes"
            name="notes"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 border-t border-gray-200 pt-4">
          <button
            type="button"
            onClick={handleCancel}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            {isSubmitting ? "Creating..." : "Create Statement"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default StatementCreate;
